import React, { useEffect, useState } from "react";
import { Loader2, Save, AlertTriangle, Trash2, RefreshCw, Database, Mail, ShieldCheck, PieChart, MessageSquareText, Send } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, unwrap } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [
  { key: "business_name", label: "Business name" },
  { key: "tagline", label: "Tagline" },
  { key: "support_email", label: "Support email" },
  { key: "support_phone", label: "Support phone" },
  { key: "company_phone", label: "Company phone", hint: "Business phone shown in the public footer, Contact page and transactional email footers. e.g. +1 508-397-0721" },
  { key: "whatsapp_number", label: "WhatsApp number", hint: "E.164 format with country code, e.g. +18572346065. Used by the floating WhatsApp button and the Contact page. Leave empty to hide WhatsApp everywhere." },
  { key: "contact_name", label: "Contact name" },
  { key: "hours", label: "Hours", hint: "e.g. 7:00 AM — 10:00 PM" },
  { key: "days_open", label: "Days open", hint: "e.g. Monday — Sunday" },
  { key: "ga4_id", label: "Google Analytics 4 ID" },
  { key: "meta_pixel_id", label: "Meta Pixel ID" },
  { key: "stripe_public_key", label: "Stripe publishable key" },
];

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setData(unwrap(r).value)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const update = (key, val) => setData((d) => ({ ...d, [key]: val }));

  const save = async () => {
    const cl = Number(data.commission_split_cleaner ?? 45);
    const pl = Number(data.commission_split_platform ?? 55);
    if (Math.abs(cl + pl - 100) > 0.01) {
      toast.error(`Commission split must add up to 100% (currently ${(cl + pl).toFixed(0)}%)`);
      return;
    }
    setSaving(true);
    try {
      await api.patch("/admin/settings", data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Save failed");
    }
    finally { setSaving(false); }
  };

  if (loading || !data) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold tracking-tighter">Settings</h1>
        <Button onClick={save} disabled={saving} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}
        </Button>
      </div>

      <Card className={`rounded-2xl p-6 border ${data.maintenance_mode ? "border-amber-300 bg-amber-50/40" : "border-[#E5E7EB]"}`} data-testid="maintenance-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`w-10 h-10 rounded-xl grid place-items-center ${data.maintenance_mode ? "bg-amber-200 text-amber-800" : "bg-[#F5F5F4] text-[#4B5563]"}`}>
              <AlertTriangle className="w-5 h-5" />
            </span>
            <div>
              <div className="font-display font-bold">Maintenance mode</div>
              <div className="text-xs text-[#4B5563]">Blocks new bookings with a custom message.</div>
            </div>
          </div>
          <Switch checked={!!data.maintenance_mode}
            onCheckedChange={(v) => update("maintenance_mode", v)} data-testid="maintenance-toggle" />
        </div>
        {data.maintenance_mode && (
          <div className="mt-4">
            <Label className="text-sm">Message shown to customers</Label>
            <Textarea rows={3} value={data.maintenance_message || ""}
              onChange={(e) => update("maintenance_message", e.target.value)}
              placeholder="We're temporarily not taking bookings — back soon."
              className="rounded-xl mt-1" data-testid="maintenance-message" />
          </div>
        )}
      </Card>

      <Card className="rounded-2xl p-6 border border-[#E5E7EB] space-y-4">
        <div className="rounded-xl bg-[#0EA5A4]/8 border border-[#0EA5A4]/20 px-4 py-3" data-testid="settings-live-note">
          <p className="text-sm text-[#0F766E] font-semibold">These fields are live — no redeploy needed.</p>
          <p className="text-xs text-[#4B5563] mt-0.5">When you Save, the public site (footer, Contact page, floating WhatsApp button, About page and Schema.org) updates instantly for every visitor.</p>
        </div>
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-sm">{f.label}</Label>
            {f.hint && <p className="text-xs text-[#9CA3AF] mt-0.5">{f.hint}</p>}
            <Input value={data[f.key] || ""} onChange={(e) => update(f.key, e.target.value)}
              className="rounded-xl mt-1" data-testid={`settings-${f.key}`} />
          </div>
        ))}
        <div data-testid="settings-about-copy-block">
          <Label className="text-sm">About page copy</Label>
          <p className="text-xs text-[#9CA3AF] mt-0.5">Shown on /about. Split paragraphs with a blank line. Leave empty to show the default copy.</p>
          <Textarea rows={6} value={data.about_copy || ""}
            onChange={(e) => update("about_copy", e.target.value)}
            placeholder="Tell your story here — founders, year founded, milestones, team size…"
            className="rounded-xl mt-2" data-testid="settings-about-copy" />
        </div>
      </Card>

      <EmailToggles data={data} update={update} />

      <SmsSettingsCard data={data} update={update} />

      <CommissionSplitCard data={data} update={update} />

      <BgCheckLegalReviewCard data={data} update={update} />

      <MaintenanceTools />
    </div>
  );
}

/** Marketplace commission split + assignment timeout (Fase 3, Bloque F). */
function CommissionSplitCard({ data, update }) {
  const cleanerPct = data.commission_split_cleaner ?? 45;
  const platformPct = data.commission_split_platform ?? 55;
  const sum = Number(cleanerPct) + Number(platformPct);
  const valid = Math.abs(sum - 100) <= 0.01;

  // Editing one side auto-balances the other so the invariant is hard to break.
  const setCleaner = (v) => {
    const n = v === "" ? "" : Number(v);
    update("commission_split_cleaner", n);
    if (n !== "" && !Number.isNaN(n)) update("commission_split_platform", Math.round((100 - n) * 100) / 100);
  };
  const setPlatform = (v) => {
    const n = v === "" ? "" : Number(v);
    update("commission_split_platform", n);
    if (n !== "" && !Number.isNaN(n)) update("commission_split_cleaner", Math.round((100 - n) * 100) / 100);
  };

  return (
    <Card className="rounded-2xl p-6 border border-[#E5E7EB]" data-testid="commission-split-card">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#0EA5A4]/10 text-[#0F766E] grid place-items-center">
          <PieChart className="w-5 h-5" />
        </span>
        <div>
          <div className="font-display font-bold">Commission split</div>
          <div className="text-xs text-[#4B5563]">Applies to new bookings only. Paid bookings keep their split.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
        <div>
          <Label className="text-sm">Cleaner share</Label>
          <div className="relative mt-1">
            <Input type="number" min="0" max="100" step="1" value={cleanerPct}
              onChange={(e) => setCleaner(e.target.value)}
              className="rounded-xl pr-8" data-testid="commission-cleaner-input" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#9CA3AF]">%</span>
          </div>
        </div>
        <div>
          <Label className="text-sm">Platform share</Label>
          <div className="relative mt-1">
            <Input type="number" min="0" max="100" step="1" value={platformPct}
              onChange={(e) => setPlatform(e.target.value)}
              className="rounded-xl pr-8" data-testid="commission-platform-input" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#9CA3AF]">%</span>
          </div>
        </div>
      </div>

      <div className={`mt-3 text-xs font-semibold ${valid ? "text-emerald-700" : "text-rose-600"}`} data-testid="commission-sum-hint">
        {valid ? `Total: ${sum.toFixed(0)}% ✓` : `Total: ${sum.toFixed(0)}% — cleaner and platform must add up to 100%`}
      </div>

      <div className="mt-6 pt-5 border-t border-[#E5E7EB]">
        <Label className="text-sm">Assignment response window</Label>
        <p className="text-xs text-[#9CA3AF] mt-0.5">
          Hours a cleaner has to accept or decline before the assignment is released automatically
          and the admin is notified. Checked every 15 minutes.
        </p>
        <div className="relative mt-2 max-w-[200px]">
          <Input type="number" min="0.25" max="168" step="1" value={data.assignment_timeout_hours ?? 24}
            onChange={(e) => update("assignment_timeout_hours", e.target.value === "" ? "" : Number(e.target.value))}
            className="rounded-xl pr-14" data-testid="assignment-timeout-input" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#9CA3AF]">hours</span>
        </div>
      </div>
    </Card>
  );
}

const EMAIL_TOGGLES = [  { key: "emails_customer_enabled", label: "Customer emails", hint: "Booking confirmations, coordination updates, cleaner assigned, review invites." },
  { key: "emails_cleaner_enabled", label: "Cleaner emails", hint: "Application received, approved (with login), rejected." },
  { key: "emails_admin_enabled", label: "Admin notifications", hint: "New booking / coordination / application alerts to your support inbox." },
  { key: "email_bcc_enabled", label: "BCC a copy to support inbox", hint: "Sends a hidden copy of every customer & cleaner email to the support address." },
];

function EmailToggles({ data, update }) {
  return (
    <Card className="rounded-2xl p-6 border border-[#E5E7EB] space-y-4" data-testid="email-settings-card">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#0EA5A4]/10 text-[#0F766E] grid place-items-center">
          <Mail className="w-5 h-5" />
        </span>
        <div>
          <div className="font-display font-bold">Email notifications</div>
          <div className="text-xs text-[#4B5563]">Silence whole blocks while testing. Turning a block off stops those emails immediately.</div>
        </div>
      </div>
      <div className="divide-y divide-[#F1F1F0]">
        {EMAIL_TOGGLES.map((t) => (
          <div key={t.key} className="flex items-center justify-between gap-4 py-3">
            <div>
              <div className="text-sm font-semibold">{t.label}</div>
              <div className="text-xs text-[#9CA3AF] mt-0.5">{t.hint}</div>
            </div>
            <Switch
              checked={data[t.key] !== false}
              onCheckedChange={(v) => update(t.key, v)}
              data-testid={`email-toggle-${t.key}`}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}

const SMS_TOGGLES = [
  { key: "sms_confirmation_enabled", label: "Booking confirmation", hint: "Sent right after payment, alongside the confirmation email." },
  { key: "sms_reminder_enabled", label: "Day-before reminder", hint: "Sent once daily at 10 AM ET for cleanings scheduled tomorrow." },
  { key: "sms_on_the_way_enabled", label: "Cleaner on the way", hint: "Sent the moment the cleaner checks in at the property." },
];

function SmsSettingsCard({ data, update }) {
  const [testPhone, setTestPhone] = useState("");
  const [sending, setSending] = useState(false);
  const configured = !!data.sms_twilio_configured;

  const sendTest = async () => {
    if (!testPhone.trim()) { toast.error("Enter a phone number first"); return; }
    setSending(true);
    try {
      const r = await api.post("/admin/sms/test", { phone: testPhone.trim() });
      const res = unwrap(r);
      toast.success(configured ? `Test SMS sent via ${res.transport}` : "Logged (Twilio not configured yet) — check backend logs");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not send test SMS");
    } finally { setSending(false); }
  };

  return (
    <Card className="rounded-2xl p-6 border border-[#E5E7EB] space-y-4" data-testid="sms-settings-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-[#0EA5A4]/10 text-[#0F766E] grid place-items-center">
            <MessageSquareText className="w-5 h-5" />
          </span>
          <div>
            <div className="font-display font-bold">SMS (Twilio)</div>
            <div className="text-xs text-[#4B5563]">Master switch starts off — turn it on once A2P 10DLC registration is approved.</div>
          </div>
        </div>
        <span
          className={`text-xs font-semibold px-2.5 py-1 rounded-full ${configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}
          data-testid="sms-twilio-status">
          {configured ? "Twilio connected" : "Twilio not configured — messages only logged"}
        </span>
      </div>

      <div className="flex items-center justify-between gap-4 py-3 border-t border-[#F1F1F0]">
        <div>
          <div className="text-sm font-semibold">Enable SMS</div>
          <div className="text-xs text-[#9CA3AF] mt-0.5">Master switch. While off, nothing is sent — everything is only logged.</div>
        </div>
        <Switch checked={!!data.sms_enabled} onCheckedChange={(v) => update("sms_enabled", v)} data-testid="sms-master-toggle" />
      </div>

      <div className="divide-y divide-[#F1F1F0]">
        {SMS_TOGGLES.map((t) => (
          <div key={t.key} className="flex items-center justify-between gap-4 py-3">
            <div>
              <div className="text-sm font-semibold">{t.label}</div>
              <div className="text-xs text-[#9CA3AF] mt-0.5">{t.hint}</div>
            </div>
            <Switch checked={data[t.key] !== false} onCheckedChange={(v) => update(t.key, v)} data-testid={`sms-toggle-${t.key}`} />
          </div>
        ))}
      </div>

      <div className="pt-4 border-t border-[#E5E7EB]">
        <Label className="text-sm">Send test SMS</Label>
        <p className="text-xs text-[#9CA3AF] mt-0.5">Sends immediately regardless of the switches above — useful to confirm Twilio the day you connect it.</p>
        <div className="flex gap-2 mt-2 max-w-sm">
          <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+1 555 123 4567"
            className="rounded-xl" data-testid="sms-test-phone-input" />
          <Button onClick={sendTest} disabled={sending} variant="outline" className="rounded-xl shrink-0" data-testid="sms-test-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function BgCheckLegalReviewCard({ data, update }) {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const reviewed = !!data.bg_check_legal_reviewed;

  const toggle = async (val) => {
    update("bg_check_legal_reviewed", val);
    try {
      await api.patch("/admin/settings", { bg_check_legal_reviewed: val });
      toast.success(val ? "Marked as legally reviewed" : "Marked as pending legal review");
    } catch (err) {
      update("bg_check_legal_reviewed", !val);
      toast.error(err?.response?.data?.detail?.message || "Could not update — owner role required");
    }
  };

  return (
    <Card
      className={`rounded-2xl p-6 border ${reviewed ? "border-[#E5E7EB]" : "border-amber-300 bg-amber-50/40"}`}
      data-testid="bg-check-legal-review-card"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={`w-10 h-10 rounded-xl grid place-items-center ${reviewed ? "bg-emerald-100 text-emerald-700" : "bg-amber-200 text-amber-800"}`}>
            <ShieldCheck className="w-5 h-5" />
          </span>
          <div>
            <div className="font-display font-bold">Background check — legal review</div>
            <div className="text-xs text-[#4B5563] max-w-md">
              Controls the "Legal review pending" banner on the Background Check section of every
              application. Flip only after Massachusetts counsel signs off on the disclosure,
              authorization and adverse-action text.
            </div>
          </div>
        </div>
        <Switch
          checked={reviewed}
          disabled={!isOwner}
          onCheckedChange={toggle}
          data-testid="bg-check-legal-reviewed-toggle"
        />
      </div>
      {!isOwner && (
        <p className="text-xs text-[#9CA3AF] mt-3" data-testid="bg-check-legal-review-owner-note">
          Only an account owner can change this. Ask an owner to promote you from /admin/users if needed.
        </p>
      )}
    </Card>
  );
}

function MaintenanceTools() {
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/maintenance/test-data");
      setCounts(unwrap(r));
    } catch { toast.error("Could not load maintenance counters"); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const purge = async () => {
    if (!counts) return;
    const total = counts.bookings + counts.waitlist + counts.contact_messages + counts.reviews;
    if (total === 0) { toast.info("Nothing to purge — DB is already clean."); return; }
    const ok = window.confirm(
      `This will permanently delete ${total} test rows:\n` +
      `• ${counts.bookings} bookings\n• ${counts.waitlist} waitlist signups\n` +
      `• ${counts.contact_messages} contact messages\n• ${counts.reviews} reviews\n\nProceed?`
    );
    if (!ok) return;
    setPurging(true);
    try {
      const r = await api.post("/admin/maintenance/test-data/purge");
      const res = unwrap(r);
      toast.success(`Purged ${res.bookings_deleted + res.waitlist_deleted + res.contact_messages_deleted + res.reviews_deleted} rows`);
      await refresh();
    } catch { toast.error("Purge failed"); }
    finally { setPurging(false); }
  };

  const reseed = async () => {
    const ok = window.confirm(
      "Force settings to the production values (business_name, support_phone, support_email, hours, contact_name)?\nOther fields (about_copy, GA4 ID, Meta Pixel, maintenance flag) are preserved."
    );
    if (!ok) return;
    setSeeding(true);
    try {
      await api.post("/admin/maintenance/seed-settings");
      toast.success("Settings re-seeded — reload to see the new values");
    } catch { toast.error("Re-seed failed"); }
    finally { setSeeding(false); }
  };

  return (
    <Card className="rounded-2xl p-6 border border-amber-200 bg-amber-50/30" data-testid="maintenance-tools-card">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-amber-200 text-amber-800 grid place-items-center">
          <Database className="w-5 h-5" />
        </span>
        <div className="flex-1">
          <div className="font-display font-bold">Maintenance tools</div>
          <p className="text-xs text-[#4B5563] mt-0.5">
            Cleanup utilities for QA-residual data and a one-click re-seed of company settings to production values.
          </p>
        </div>
        <Button onClick={refresh} disabled={loading} variant="outline" size="sm" className="rounded-xl" data-testid="maintenance-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {counts && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="maintenance-counts">
          {[
            { k: "bookings", l: "Test bookings" },
            { k: "waitlist", l: "Test waitlist" },
            { k: "contact_messages", l: "Test contacts" },
            { k: "reviews", l: "Test reviews" },
          ].map(({ k, l }) => (
            <div key={k} className="bg-white rounded-xl p-3 border border-amber-200">
              <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold">{l}</div>
              <div className="font-mono text-2xl font-bold mt-1" data-testid={`maintenance-count-${k}`}>{counts[k]}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 flex gap-2 flex-wrap">
        <Button onClick={purge} disabled={purging || !counts} variant="outline"
          className="rounded-xl border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="maintenance-purge-btn">
          {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-2" /> Purge test data</>}
        </Button>
        <Button onClick={reseed} disabled={seeding} variant="outline" className="rounded-xl" data-testid="maintenance-reseed-btn">
          {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-2" /> Re-seed company settings</>}
        </Button>
      </div>
    </Card>
  );
}
